import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/auth-context';
import type { Task, Pulse, Sequence, ChatMessage } from '@shared/schema';
import { isEmailWorkbenchData } from '@/lib/email-workbench-cache';

export interface PulseWorkbenchData {
  pulse: Pulse | null;
  tasks: Task[];
  sequences: Array<{ sequenceNumber: string; sequenceName: string }>;
  chatMessages: ChatMessage[];
  permissions: {
    canEdit: boolean;
    canCreateTasks: boolean;
    canDeleteTasks: boolean;
    canAssignSequences: boolean;
  };
  projectId: number | null;
}

interface UsePulseWorkbenchOptions {
  enabled?: boolean;
}

export function usePulseWorkbench(pulseId: number | null, options: UsePulseWorkbenchOptions = {}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { enabled = true } = options;

  const queryKey = ['pulse-workbench', pulseId];

  const query = useQuery<PulseWorkbenchData>({
    queryKey,
    queryFn: async () => {
      if (!pulseId) {
        console.log('[Workbench Hook] No pulseId provided, returning empty data');
        return {
          pulse: null,
          tasks: [],
          sequences: [],
          chatMessages: [],
          permissions: {
            canEdit: false,
            canCreateTasks: false,
            canDeleteTasks: false,
            canAssignSequences: false,
          },
          projectId: null,
        };
      }

      console.log(`[Workbench Hook] Fetching data for pulse ${pulseId}`);
      
      // Use sovereign workbench endpoint for atomic data fetch
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      const response = await fetch(`/api/pulses/${pulseId}/workbench`, { 
        credentials: 'include',
        headers,
      });
      
      if (!response.ok) {
        console.error(`[Workbench Hook] Failed to fetch: ${response.status}`);
        throw new Error(`Failed to fetch workbench data: ${response.status}`);
      }
      
      const data = await response.json();
      
      console.log(`[Workbench Hook] Received: pulse=${data.pulse?.pulseDescription}, tasks=${data.tasks?.length || 0}, messages=${data.chatMessages?.length || 0}`);
      
      return {
        pulse: data.pulse ?? null,
        tasks: data.tasks ?? [],
        sequences: data.sequences ?? [],
        chatMessages: data.chatMessages ?? [],
        permissions: data.permissions ?? {
          canEdit: false,
          canCreateTasks: false,
          canDeleteTasks: false,
          canAssignSequences: false,
        },
        projectId: data.projectId ?? null,
      };
    },
    enabled: enabled && pulseId !== null,
    // Email HTML can change after the initial fetch (CID attachment workers).
    // A fresh read on reopen/reenable catches broadcasts missed while closed.
    // Other workbenches retain their existing infinite-cache behavior.
    staleTime: query => isEmailWorkbenchData(query.state.data) ? 0 : Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: query => isEmailWorkbenchData(query.state.data),
  });

  const updateTasks = (updater: (tasks: Task[]) => Task[]) => {
    queryClient.setQueryData<PulseWorkbenchData>(queryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        tasks: updater(old.tasks),
      };
    });
  };

  const addTasks = (newTasks: Task[]) => {
    updateTasks((tasks) => [...tasks, ...newTasks]);
  };

  const removeTask = (taskId: number) => {
    updateTasks((tasks) => tasks.filter((t) => t.id !== taskId));
  };

  const updateTask = (taskId: number, updates: Partial<Task>) => {
    updateTasks((tasks) =>
      tasks.map((t) => (t.id === taskId ? { ...t, ...updates } : t))
    );
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  const refetch = () => {
    return query.refetch();
  };

  const addChatMessage = (message: ChatMessage) => {
    queryClient.setQueryData<PulseWorkbenchData>(queryKey, (old) => {
      if (!old) return old;
      const existingIds = new Set(old.chatMessages.map((m) => m.id));
      if (existingIds.has(message.id)) return old;
      return {
        ...old,
        chatMessages: [...old.chatMessages, message],
      };
    });
  };

  const updateChatMessage = (messageId: number, updates: Partial<ChatMessage>) => {
    queryClient.setQueryData<PulseWorkbenchData>(queryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        chatMessages: old.chatMessages.map((m) =>
          m.id === messageId ? { ...m, ...updates } : m
        ),
      };
    });
  };

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    pulse: query.data?.pulse ?? null,
    tasks: query.data?.tasks ?? [],
    sequences: query.data?.sequences ?? [],
    chatMessages: query.data?.chatMessages ?? [],
    permissions: query.data?.permissions ?? {
      canEdit: false,
      canCreateTasks: false,
      canDeleteTasks: false,
      canAssignSequences: false,
    },
    projectId: query.data?.projectId ?? null,
    updateTasks,
    addTasks,
    removeTask,
    updateTask,
    addChatMessage,
    updateChatMessage,
    invalidate,
    refetch,
    queryKey,
  };
}
